import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { experiences } from '../data/experiences';
export function Experiences() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    const items = Array.from(el.children) as HTMLElement[];
    let nearest = 0;
    let best = Infinity;
    items.forEach((item, i) => {
      const d = Math.abs(item.offsetLeft - el.scrollLeft);
      if (d < best) {
        best = d;
        nearest = i;
      }
    });
    setActive(nearest);
  }, []);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);
  const goTo = (i: number) => {
    const el = trackRef.current;
    const item = el?.children[i] as HTMLElement | undefined;
    if (el && item) el.scrollTo({
      left: item.offsetLeft,
      behavior: 'smooth'
    });
  };
  const arrowClass = 'absolute top-[32%] z-10 hidden h-16 w-9 items-center justify-center bg-ink text-paper transition-opacity duration-300 hover:opacity-80 md:flex';
  return <section id="experiences" aria-labelledby="experiences-heading" className="scroll-mt-20 bg-paper px-6 pb-24 md:px-16 md:pb-36" data-id="element-425" data-mp="5vfuyjrg5rnv">
      <h2 id="experiences-heading" className="font-editorial text-[34px] font-normal leading-[38px] tracking-[-0.01em] md:text-[44px] md:leading-[48px]" data-id="element-426" data-mp="2vlautu3x01po">
        Moments few will ever know
      </h2>

      <div className="relative mt-10" data-id="element-427" data-mp="1imr2wyq92zti">
        <ul ref={trackRef} className="flex snap-x snap-mandatory gap-5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-428" data-mp="1mnb0g3eqk5g6">
          {experiences.map(exp => <li key={exp.title} className={`shrink-0 snap-start ${exp.featured ? 'w-[86%] md:w-[52%] lg:w-[46%]' : 'w-[86%] md:w-[36%] lg:w-[29%]'}`} data-id="element-429" data-mp="1dmd3u66mn07m">
              <Link to={`/experiences/${exp.slug}`} className="group flex h-full flex-col border border-line bg-paper" data-id="element-430" data-mp="13pi8tx9kbhy3">
                <div className="h-[240px] overflow-hidden bg-mist md:h-[300px] lg:h-[360px]" data-id="element-431" data-mp="pz519w0l259a">
                  <img src={exp.image} alt={exp.alt} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none" data-id="element-432" data-mp="uwn2t1c9ti79" />
                </div>
                <div className="flex flex-1 flex-col px-6 pb-8 pt-7 md:px-8" data-id="element-433" data-mp="1d72qmwlzn9o">
                  <p className="text-[14px] font-light text-ink/70" data-id="element-434" data-mp="3tgzx4lgswm0d">{exp.eyebrow}</p>
                  <h3 className={`mt-2 font-editorial font-normal tracking-[-0.01em] ${exp.featured ? 'text-[28px] leading-[33px] md:text-[34px] md:leading-[39px]' : 'text-[24px] leading-[29px] md:text-[27px] md:leading-[32px]'}`} data-id="element-435" data-mp="bb4pt72qfe0p">
                    {exp.title}
                  </h3>
                  <span className="mt-auto flex items-center gap-3 pt-8 text-[15px] text-ink/80" data-id="element-436" data-mp="u01jp9u7w4ie">
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} aria-hidden="true" data-id="element-437" data-mp="p31e101a53rt" />
                    {exp.cta}
                  </span>
                </div>
              </Link>
            </li>)}
        </ul>

        {canPrev && <button type="button" onClick={() => goTo(Math.max(0, active - 1))} aria-label="Previous experience" className={`${arrowClass} left-0`} data-id="element-438" data-mp="2xgy4qjj1zpp2">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-439" data-mp="32b2d977yw5s9" />
          </button>}
        {canNext && <button type="button" onClick={() => goTo(Math.min(experiences.length - 1, active + 1))} aria-label="Next experience" className={`${arrowClass} right-0`} data-id="element-440" data-mp="29kaj0fagpr7k">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-441" data-mp="2jhyoo09lwj4i" />
          </button>}
      </div>

      <div className="mt-10 flex justify-center gap-2.5" role="tablist" aria-label="Choose experience" data-id="element-442" data-mp="2oicsf646d6wq">
        {experiences.map((exp, i) => <button key={exp.title} type="button" role="tab" aria-selected={i === active} aria-label={`Show ${exp.eyebrow}`} onClick={() => goTo(i)} className="p-1" data-id="element-443" data-mp="3uzq0v9g6v78z" data-mpi={i}>
            <span className={`block h-2 w-2 border border-ink transition-colors duration-300 ${i === active ? 'bg-ink' : 'bg-transparent'}`} data-id="element-444" data-mp="1jbctw06oeinq" />
          </button>)}
      </div>
    </section>;
}